import { useNavigate, useParams } from "react-router-dom";

import toast from "react-hot-toast";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import { axiosWithToast, ItemStatus } from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";
import { QuillEditor } from "../../../components/utils/quill-editor";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import ModernAbsenceTypeSelector from "../../../components/utils/Selector/ModernAbsenceTypeSelector";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useSelector } from "react-redux";
import {
  createAbsence,
  deleteAbsence,
  fetchAbsence,
  updateAbsence,
  exportAbsencePDF,
} from "../../../api/absence/absenece";
import { PMDatePicker } from "../../../components/utils/PMDatePicker";
import { fetchAbsenceType } from "../../../api/absence/type";
import { PMNotice } from "../../../components/utils/notifications";
import { DocumentCenter } from "../../../components/dashboard/documents/document-center";
import { CheckBadgeIcon, TrashIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMButton } from "../../../components/utils/PMButton";
import PMSwitch from "../../../components/utils/PMSwitch";
import ProConfirmation from "../../../components/utils/ProConfirmation";

export const EditAbsence = () => {
  const { t } = useTranslation();

  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const isAdmin = useUserHasAccess({ role: "absence", action: "approve" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const isSuperAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const dateToday = new Date().toISOString().split("T")[0];
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [confirmation, setConfirmation] = useState({
    item_id: null,
    module: null,
    action: null,
  });

  const navigate = useNavigate(); // Assuming you have a navigate function from react-router-dom

  const [absence, setAbsence] = useState(null);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [absenceTypeSelectorOpen, setAbsenceTypeSelectorOpen] = useState(false);
  const [employeeSelectorOpen, setEmployeeSelectorOpen] = useState(false);
  
  // Check if absence is approved/accepted and should be read-only
  const isApproved = absence?.accepted || absence?.status === "approved";
  const isReadOnly = isApproved && !isAdmin; // Only admins can edit approved absences
  const [initialValues, setInitialValues] = useState({
    id: null,
    description: "",
    absence_type_id: null,
    user_id: currentUser?.id || null, // Always set to current user for non-admin users
    date_from: dateToday,
    date_too: "",
    submit: null,
  });

  useEffect(() => {
    // if ID is set, fetch the absence
    if (id) {
      getAbsence(id);
    } else {
      setIsLoading(false);
      setAbsence(null);
      // Reset to default values for new absence
      setInitialValues({
        id: null,
        description: "",
        absence_type_id: null,
        user_id: currentUser?.id || null, // Always set to current user for non-admin users
        date_from: dateToday,
        date_too: "",
        submit: null,
      });
      setSelectedAbsenceType(null);
    }
  }, [id]);

  useEffect(() => {
    if (absence && absence.id) {
      // Only update initialValues if we have a valid absence with an ID
      const absenceTypeId = absence.absence_type?.id || absence.absence_type_id;
      
      setInitialValues({
        id: absence.id,
        description: absence.description || "",
        absence_type_id: absenceTypeId || null,
        user_id: isAdmin && absence.user?.id ? absence.user.id : (isAdmin ? currentUser?.id : absence.user?.id),
        date_from: absence.date_from || dateToday,
        date_too: absence.date_too || "",
        submit: null,
      });
      
      // Always fetch the full absence_type object to ensure we have all fields (like max_duration_days)
      // The API response might only include id and displayTitle, but we need max_duration_days for validation
      if (absenceTypeId) {
        fetchAbsenceType(absenceTypeId)
          .then((response) => {
            // fetchAbsenceType returns the object directly, not wrapped in { data: ... }
            const absenceTypeData = response?.data || response;
            if (absenceTypeData && absenceTypeData.id) {
              setSelectedAbsenceType(absenceTypeData);
            } else {
              // Fallback to partial data if fetch fails
              if (absence.absence_type) {
                setSelectedAbsenceType(absence.absence_type);
              }
            }
          })
          .catch((err) => {
            // Fallback to partial data if fetch fails
            if (absence.absence_type) {
              setSelectedAbsenceType(absence.absence_type);
            }
          });
      } else {
        // No absence type, reset it
        setSelectedAbsenceType(null);
      }
    }
  }, [absence, isAdmin, currentUser?.id, dateToday]);

  // Fallback: Fetch absence type details when absence_type_id changes in initialValues
  // This handles cases where absence was loaded but absence_type wasn't set properly
  // NOTE: We use a ref to track if we're in the middle of a user selection to avoid resetting
  const isUserSelectingRef = useRef(false);
  
  useEffect(() => {
    // Don't reset if user is actively selecting (onChange was just called)
    if (isUserSelectingRef.current) {
      isUserSelectingRef.current = false;
      return;
    }
    
    if (initialValues.absence_type_id && !selectedAbsenceType) {
      // Only fetch if we don't already have the absence type
      fetchAbsenceType(initialValues.absence_type_id)
        .then((response) => {
          // fetchAbsenceType returns the object directly, not wrapped in { data: ... }
          const absenceTypeData = response?.data || response;
          if (absenceTypeData && absenceTypeData.id) {
            setSelectedAbsenceType(absenceTypeData);
          }
        })
        .catch((err) => {
          // Silently fail - errors are handled elsewhere
        });
    } else if (!initialValues.absence_type_id && selectedAbsenceType && !absence?.id) {
      // Only reset if absence_type_id was removed AND we're not editing an existing absence
      // (when editing, initialValues might not have absence_type_id yet but absence object does)
      setSelectedAbsenceType(null);
    }
  }, [initialValues.absence_type_id, selectedAbsenceType, absence?.id]);

  const getAbsence = async (id) => {
    setIsLoading(true);
    setError(false);
    try {
      const result = await fetchAbsence(id);
      if (result.success && result.data) {
        const absenceData = result.data;
        setAbsence(absenceData);
        setIsLoading(false);
      } else {
        toast.error(result.errors?.join(", ") || "Kunne ikke laste inn fravær");
        setIsLoading(false);
        setError(true);
      }
    } catch (err) {
      toast.error("Noe gikk galt ved lasting av fravær");
      setIsLoading(false);
      setError(true);
    }
  };

  const createOrUpdate = async (data) => {
    // Ensure user_id is included in the payload
    const payload = {
      ...data,
      user_id: data.user_id || (absence?.user?.id) || (absence?.user_id),
    };
    
    // Validate that user_id is present
    if (!payload.user_id) {
      const errorMsg = "user_id er påkrevd for å opprette/oppdatere fravær";
      setErrors([errorMsg]);
      throw new Error(errorMsg);
    }
    
    const result = absence?.id
      ? await updateAbsence(payload)
      : await createAbsence(payload, { fields: "*" });
    
    // Handle new API response format
    if (result.success === false) {
      // Extract errors from the new format
      const errorMessages = result.errors || [];
      setErrors(errorMessages);
      // Don't show success toast when there are errors
      throw new Error(errorMessages.join(", "));
    }
    
    // Additional check: if result has errors array even when success is true, treat as error
    if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
      setErrors(result.errors);
      throw new Error(result.errors.join(", "));
    }
    
    // Check if result.data contains errors (edge case)
    if (result.data && result.data.errors && Array.isArray(result.data.errors) && result.data.errors.length > 0) {
      setErrors(result.data.errors);
      throw new Error(result.data.errors.join(", "));
    }
    
    // Validate that we have actual absence data, not just an errors object
    if (!result.data || (result.data.errors && Array.isArray(result.data.errors))) {
      const errorMessages = result.data?.errors || ["Ukjent feil ved lagring av fravær"];
      setErrors(errorMessages);
      throw new Error(errorMessages.join(", "));
    }
    
    return result.data;
  };

  // get userType form searchParams
  const searchParams = new URLSearchParams(location.search);
  const userType = searchParams.get("userType") || "";

  const [errors, setErrors] = useState([]);

  // remove errors after 6 seconds
  useEffect(() => {
    if (errors && errors.length > 0) {
      setTimeout(() => {
        setErrors([]);
      }, 20000);
    }
  }, [errors]);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const canApprove = () => {
    if (isAdmin)
      if (!absence?.accepted) {
        return true;
      }
    return false;
  };

  const handleExportPDF = async () => {
    if (isExportingPDF) return; // Prevent multiple clicks
    
    if (!absence?.id) {
      toast.error("Fraværet må være lagret før PDF kan eksporteres");
      return;
    }
    
    if (!absence?.accepted && absence?.status !== "approved") {
      toast.error("Fraværet må være godkjent før PDF kan eksporteres");
      return;
    }

    setIsExportingPDF(true);
    try {
      const result = await exportAbsencePDF(absence.id);
      
      if (result.success && result.data) {
        // Check if result.data is a Blob
        let blob;
        if (result.data instanceof Blob) {
          blob = result.data;
        } else {
          blob = new Blob([result.data], { type: 'application/pdf' });
        }
        
        // Validate PDF blob
        if (blob.size < 100) {
          throw new Error("PDF-filen er for liten eller ugyldig");
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fravær_${absence.id}_${absence.date_from}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF ble eksportert");
      } else {
        const errorMsg = result.errors?.join(", ") || "Kunne ikke eksportere PDF";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(error.message || "Kunne ikke eksportere PDF");
    } finally {
      setIsExportingPDF(false);
    }
  };

  const headerActions = [
    {
      component: null,
      label: isExportingPDF ? "Eksporterer..." : "Eksporter PDF",
      description: "",
      path: null,
      action: handleExportPDF,
      icon: DocumentArrowDownIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !absence?.id || (!absence?.accepted && absence?.status !== "approved"),
      menu: false,
      disabled: isExportingPDF,
    },
    {
      component: null,
      label: "Godkjenne",
      description: "",
      path: null,
      action: () => setApproveDialogOpen(true),
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !canApprove(),
      menu: false,
    },
    {
      component: null,
      label: "Slett",
      description: "",
      path: null,
      action: () => setDeleteConfirmationOpen(true),
      icon: TrashIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !isAdmin,
      menu: true,
    },
  ];

  const headerMeta = [
    {
      label: "wd",
      component: <ItemStatus item={absence} />,
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: "Godkjent av " + absence?.approved_by?.displayTitle,
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: !absence?.accepted,
    },
  ];

  const handleDeleteAbsence = () => {
    setDeleteConfirmationOpen(false);
    setIsLoading(true);
    // delete absence then navigate to absence list
    deleteAbsence(absence.id)
      .then(() => {
        setIsLoading(false);
        navigate("/dashboard/absences/overview");
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Something went wrong when trying to delete the absence");
      });
  };

  function Content() {
    return (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            <Formik
              enableReinitialize={true} // Enable reinitialization when initialValues change
              initialValues={{
                ...initialValues,
                // Ensure user_id is always set for non-admin users
                user_id: initialValues.user_id || (!isAdmin ? currentUser?.id : null),
              }}
              validationSchema={Yup.object({
                description: Yup.string(),
                user_id: Yup.number()
                  .required("Ansatt må velges")
                  .nullable(),
              })}
              onSubmit={async (values, helpers) => {
                try {
                  // Clear any previous errors before submission
                  setErrors([]);
                  
                  // Ensure user_id is set before submitting
                  if (!values.user_id) {
                    helpers.setFieldError("user_id", "Ansatt må velges");
                    helpers.setSubmitting(false);
                    toast.error("Du må velge en ansatt før du kan lagre fraværet");
                    return;
                  }
                  
                  const data = await createOrUpdate(values);
                  
                  // Validate that data is actually absence data, not an errors object
                  if (!data || typeof data !== 'object') {
                    helpers.setSubmitting(false);
                    return;
                  }
                  
                  // Check if data contains errors (shouldn't happen, but be safe)
                  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                    setErrors(data.errors);
                    helpers.setSubmitting(false);
                    return;
                  }
                  
                  // Check if data has required absence fields
                  if (!data.id && !absence?.id) {
                    helpers.setSubmitting(false);
                    return;
                  }
                  
                  setAbsence(data);
                  setInitialValues((prev) => ({
                    ...prev,
                    ...values,
                  }));
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                  toast.success("Fravær ble lagret");
                  setErrors([]);
                  
                  // If this was a new absence, navigate to the edit page
                  if (!absence?.id && data.id) {
                    navigate(`/dashboard/absences/${data.id}`);
                  }
                } catch (err) {
                  helpers.setSubmitting(false);
                  // Errors are already set in createOrUpdate
                  // Don't show success toast when there are errors
                }
              }}
            >
              {(formik) => (
                <Form>
                  {/* Error Display */}
                  {errors && errors.length > 0 && (
                    <div className="mb-6">
                      <PMNotice
                        severity="error"
                        title="Feil ved lagring av fravær"
                        description={
                          <div className="mt-2">
                            <ul className="list-disc list-inside space-y-1">
                              {errors.map((error, index) => (
                                <li key={index} className="text-sm">
                                  {error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        }
                      />
                    </div>
                  )}

                  <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid sm:grid-cols-8 gap-6">
                        <div className="sm:col-span-2">
                          {isAdmin ? (
                            <div className="relative">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Ansatt
                              </label>
                              <button
                                type="button"
                                onClick={() => !isReadOnly && setEmployeeSelectorOpen(true)}
                                disabled={isReadOnly}
                                className={`w-full text-left px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  isReadOnly 
                                    ? "bg-gray-100 text-gray-500 cursor-not-allowed" 
                                    : "bg-white hover:bg-gray-50"
                                }`}
                              >
                                {formik.values.user_id && absence?.user
                                  ? absence.user.displayTitle
                                  : formik.values.user_id && !absence?.user
                                  ? "Laster ansatt..."
                                  : "Velg ansatt"}
                              </button>
                              {formik.errors.user_id && formik.touched.user_id && (
                                <p className="mt-1 text-xs text-red-500">
                                  {formik.errors.user_id}
                                </p>
                              )}
                              {employeeSelectorOpen && (
                                <div className="fixed inset-0 z-50 overflow-y-auto">
                                  <div className="flex min-h-screen items-center justify-center p-4">
                                    <div
                                      className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                                      onClick={() => setEmployeeSelectorOpen(false)}
                                    />
                                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
                                      <ModernEmployeeSelector
                                        open={employeeSelectorOpen}
                                        selectedEmployees={
                                          formik.values.user_id && absence?.user
                                            ? [absence.user]
                                            : []
                                        }
                                        onChange={(employees) => {
                                          if (employees.length > 0) {
                                            const selectedUser = employees[0];
                                            formik.setFieldValue("user_id", selectedUser.id);
                                            setAbsence((prev) => ({
                                              ...prev,
                                              user: selectedUser,
                                            }));
                                            // Update initialValues to keep it in sync
                                            setInitialValues(prev => ({
                                              ...prev,
                                              user_id: selectedUser.id
                                            }));
                                            setEmployeeSelectorOpen(false);
                                          } else {
                                            // If no employees selected, clear user_id
                                            formik.setFieldValue("user_id", null);
                                            setAbsence((prev) => ({
                                              ...prev,
                                              user: null,
                                            }));
                                            setInitialValues(prev => ({
                                              ...prev,
                                              user_id: null
                                            }));
                                          }
                                        }}
                                        onClose={() => setEmployeeSelectorOpen(false)}
                                        multiple={false}
                                        filters={[{ name: "EMPLOYEE", value: true }]}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Ansatt
                              </label>
                              <span className="text-lg font-medium text-gray-900">
                                {absence?.user?.displayTitle || currentUser?.displayTitle}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid sm:grid-cols-8 gap-6">
                        <div className="sm:col-span-2">
                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Fraværstype
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                if (formik.values.user_id && !isReadOnly) {
                                  setAbsenceTypeSelectorOpen(true);
                                }
                              }}
                              disabled={!formik.values.user_id || isReadOnly}
                              className={`w-full text-left px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                !formik.values.user_id || isReadOnly
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-white hover:bg-gray-50"
                              }`}
                            >
                              {selectedAbsenceType
                                ? selectedAbsenceType.displayTitle || selectedAbsenceType.title
                                : "Velg fraværstype"}
                            </button>
                            {absenceTypeSelectorOpen && (
                              <div className="fixed inset-0 z-50 overflow-y-auto">
                                <div className="flex min-h-screen items-center justify-center p-4">
                                  <div
                                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                                    onClick={() => setAbsenceTypeSelectorOpen(false)}
                                  />
                                  <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden z-[9999]">
                                    <ModernAbsenceTypeSelector
                                      open={absenceTypeSelectorOpen}
                                      selectedAbsenceType={selectedAbsenceType}
                                      userId={formik.values.user_id || currentUser?.id}
                                      onChange={(absenceType) => {
                                        // Set flag to prevent useEffect from resetting
                                        isUserSelectingRef.current = true;
                                        
                                        if (absenceType) {
                                          formik.setFieldValue("absence_type_id", absenceType.id);
                                          setSelectedAbsenceType(absenceType);
                                          setAbsenceTypeSelectorOpen(false);
                                          // Update initialValues to keep it in sync
                                          setInitialValues(prev => ({
                                            ...prev,
                                            absence_type_id: absenceType.id
                                          }));
                                          // Reset date_too if the max days changed and current selection is invalid
                                          if (formik.values.date_from && formik.values.date_too) {
                                            const start = new Date(formik.values.date_from);
                                            const end = new Date(formik.values.date_too);
                                            const diffTime = Math.abs(end - start);
                                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                            if (absenceType.max_duration_days && absenceType.code !== "compensatory_time_off" && diffDays > absenceType.max_duration_days) {
                                              formik.setFieldValue("date_too", "");
                                            }
                                          }
                                        } else {
                                          formik.setFieldValue("absence_type_id", null);
                                          setSelectedAbsenceType(null);
                                          setAbsenceTypeSelectorOpen(false);
                                          setInitialValues(prev => ({
                                            ...prev,
                                            absence_type_id: null
                                          }));
                                        }
                                      }}
                                      onClose={() => setAbsenceTypeSelectorOpen(false)}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            {!formik.values.user_id && (
                              <p className="mt-1 text-xs text-gray-500">
                                Velg ansatt først
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid sm:grid-cols-8 gap-6">
                        <div className="sm:col-span-4">
                          <PMDatePicker
                            name="date_from"
                            label={t("Dato fra")}
                            value={formik.values.date_from}
                            disabled={!formik.values.user_id || !formik.values.absence_type_id || isReadOnly}
                            onChange={(e) => {
                              formik.setFieldValue("date_from", e.target.value);
                              // Reset date_too if it's before date_from
                              if (formik.values.date_too && e.target.value > formik.values.date_too) {
                                formik.setFieldValue("date_too", "");
                              }
                            }}
                            validationRules={{
                              // Admin users can select dates in the past, others are restricted to today or future
                              minDate: isSuperAdmin ? undefined : dateToday,
                              startDate: formik.values.date_from,
                            }}
                            errors={formik.errors.date_from}
                            touched={formik.touched.date_from}
                          />
                          {(!formik.values.user_id || !formik.values.absence_type_id) && (
                            <p className="mt-1 text-xs text-gray-500">
                              {!formik.values.user_id ? "Velg ansatt først" : "Velg fraværstype først"}
                            </p>
                          )}
                        </div>
                        <div className="sm:col-span-4">
                          <PMDatePicker
                            name="date_too"
                            label={t("Dato til")}
                            value={formik.values.date_too}
                            disabled={!formik.values.user_id || !formik.values.absence_type_id || isReadOnly}
                            onChange={(e) => {
                              formik.setFieldValue("date_too", e.target.value);
                            }}
                            validationRules={{
                              minDate: formik.values.date_from || dateToday,
                              startDate: formik.values.date_from,
                              // Don't apply maxDays validation for compensatory_time_off (based on hours) or sick_leave (based on doctor's certificate, no limit)
                              maxDays: selectedAbsenceType?.code !== "compensatory_time_off" && selectedAbsenceType?.code !== "sick_leave" ? selectedAbsenceType?.max_duration_days : undefined,
                              userId: formik.values.user_id, // Pass userId for work schedule calculation
                              isDateValid: (dateStr) => {
                                // Use the latest selectedAbsenceType from state, not closure
                                const currentAbsenceType = selectedAbsenceType;
                                
                                // Skip max days validation for compensatory_time_off (based on hours) and sick_leave (based on doctor's certificate, no limit)
                                if (currentAbsenceType?.code === "compensatory_time_off" || currentAbsenceType?.code === "sick_leave") {
                                  return true;
                                }
                                
                                if (!formik.values.date_from || !currentAbsenceType?.max_duration_days) {
                                  return true;
                                }
                                
                                // If userId is provided, skip maxDays validation in frontend
                                // Backend will validate using working_days_count which respects work schedule
                                // This allows selecting dates like Friday-Monday (2 working days) even if calendar days > maxDays
                                if (formik.values.user_id) {
                                  return true; // Let backend validate using working days
                                }
                                
                                // Only use calendar days estimate when userId is NOT available
                                // This is a rough estimate for UX - backend will catch any errors
                                const start = new Date(formik.values.date_from);
                                const end = new Date(dateStr);
                                const diffTime = Math.abs(end - start);
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                const isValid = diffDays <= currentAbsenceType.max_duration_days;
                                return isValid;
                              },
                            }}
                            errors={formik.errors.date_too}
                            touched={formik.touched.date_too}
                          />
                          {(() => {
                            // Don't show max days hint for compensatory_time_off (based on hours) or sick_leave (no limit)
                            if (selectedAbsenceType?.code === "compensatory_time_off") {
                              return (
                                <p className="mt-1 text-xs text-gray-500 italic">
                                  Avspasering baserer seg på faktiske innstående timer, ikke maks antall dager
                                </p>
                              );
                            }
                            if (selectedAbsenceType?.code === "sick_leave") {
                              return (
                                <p className="mt-1 text-xs text-gray-500 italic">
                                  Sykemelding er basert på legeerklæring og har ingen begrensning på antall dager
                                </p>
                              );
                            }
                            return selectedAbsenceType?.max_duration_days ? (
                              <p className="mt-1 text-xs text-gray-500">
                                Maksimalt {selectedAbsenceType.max_duration_days}{" "}
                                {selectedAbsenceType.max_duration_days === 1 ? "dag" : "dager"} for denne fraværstypen
                              </p>
                            ) : null;
                          })()}
                          {(!formik.values.user_id || !formik.values.absence_type_id) && (
                            <p className="mt-1 text-xs text-gray-500">
                              {!formik.values.user_id ? "Velg ansatt først" : "Velg fraværstype først"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-3">
                          Beskrivelse
                        </div>

                            <div className="col-span-6">
                              <QuillEditor
                                name="description"
                                rows={20}
                                {...formik}
                                disabled={!formik.values.user_id || !formik.values.absence_type_id || isReadOnly}
                              />
                            </div>
                            {(!formik.values.user_id || !formik.values.absence_type_id) && (
                              <div className="col-span-6">
                                <p className="text-xs text-gray-500">
                                  {!formik.values.user_id ? "Velg ansatt først" : "Velg fraværstype først"}
                                </p>
                              </div>
                            )}

                        <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5" />

                  {isReadOnly && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        Dette fraværet er godkjent og kan ikke redigeres.
                      </p>
                    </div>
                  )}

                  <FormikSaveBar {...formik} disabled={isReadOnly} />
                </Form>
              )}
            </Formik>

            {absence?.id && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6">
                      <DocumentCenter
                        subtitle="Kun PDF filer er tillatt"
                        module="absence"
                        allowedFileTypes="application/pdf"
                        moduleParam={absence?.id}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle=""
        meta={headerMeta}
        actions={headerActions}
        title={
          (!absence?.id ? "Nytt" : "Redigerer") +
          " Fravær" +
          (userType ? " for " : "") +
          userType.toUpperCase()
        }
      >
        {isLoading ? <ResourceLoading /> : <Content />}
        {isAdmin && (
          <PMDialog
            title="Godkjenn fravær"
            open={approveDialogOpen}
            onClose={() => setApproveDialogOpen(false)}
          >
            <Formik
              enableReinitialize={false} // Disable automatic reinitialization
              initialValues={{
                id: absence?.id,
                accepted: absence?.accepted,
                denied: absence?.denied,
                approval_note: absence?.approval_note,
              }} // Use the separate initialValues state
              validationSchema={Yup.object({
                description: Yup.string(),
              })}
              onSubmit={async (values, helpers) => {
                try {
                  const data = await createOrUpdate(values);
                  setAbsence(data);
                  setInitialValues((prev) => ({
                    ...prev,
                    ...values,
                  }));
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                  toast.success("Fravær ble lagret");
                  setErrors([]);
                  setApproveDialogOpen(false);
                } catch (err) {
                  helpers.setSubmitting(false);
                  // Errors are already set in createOrUpdate
                }
              }}
            >
              {(formik) => (
                <Form>
                  {/* Error Display */}
                  {errors && errors.length > 0 && (
                    <div className="mb-6">
                      <PMNotice
                        severity="error"
                        title="Feil ved lagring av fravær"
                        description={
                          <div className="mt-2">
                            <ul className="list-disc list-inside space-y-1">
                              {errors.map((error, index) => (
                                <li key={index} className="text-sm">
                                  {error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        }
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                    <div className="col-span-1 sm:col-span-1">
                      <PMSwitch
                        label="Godkjent"
                        name="accepted"
                        {...formik}
                        onChange={(e) => {
                          formik.setFieldValue("denied", false);
                          formik.handleChange(e);
                        }}
                      />
                    </div>

                    <div className="col-span-1 sm:col-span-1">
                      <PMSwitch
                        label="Ikke godkjent"
                        name="denied"
                        {...formik}
                        onChange={(e) => {
                          formik.setFieldValue("accepted", false);
                          formik.handleChange(e);
                        }}
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-2">
                      <PMField
                        label="Notat"
                        name="approval_note"
                        multiline={true}
                        rows={2}
                        {...formik}
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-2 flex justify-between">
                      <PMButton
                        type="button"
                        onClick={() => setApproveDialogOpen(false)}
                        className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                      >
                        Avbryt
                      </PMButton>
                      <PMButton className="ml-10 max-w-1/2" type="submit">
                        Lagre
                      </PMButton>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </PMDialog>
        )}
      </DashboardHeader>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Er du sikkert på at du vill slette fraværet?"}
        action="delete"
        module="order_item"
        // item_id={confirmation.item_id}
        yesCallback={handleDeleteAbsence}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </>
  );
};
